'use client';

import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { Moon, Sun, Search, Atom, Users, LogIn, LogOut, Menu, X, Settings, CloudMoon, Rocket, Gamepad2, Pencil, Check } from 'lucide-react';
import { useTheme } from 'next-themes';
import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/useAuth';

const DEFAULT_VERSION = '2.1';

function getUserDisplayName(user: any) {
  return user?.displayName || user?.firstName || user?.email || 'User';
}

export function Navbar() {
  const pathname = usePathname();
  const router = useRouter();
  const { theme, setTheme, resolvedTheme } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [mounted, setMounted] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, isLoading, isAuthenticated, isOwner } = useAuth();
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      router.push(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const isActive = (path: string) => {
    if (path === '/') return pathname === '/';
    return pathname.startsWith(path);
  };

  return (
    <nav className="border-b border-primary/20 bg-card/90 backdrop-blur-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-8">
            <Link href="/" className="flex items-center gap-2 font-bold text-xl group">
              <Atom className="h-6 w-6 text-primary group-hover:animate-spin" style={{ animationDuration: '3s' }} />
              <span className="text-primary glow-text tracking-wider" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                WORLDLINES
              </span>
              <span className="text-muted-foreground text-sm font-normal hidden sm:inline">WIKI</span>
            </Link>
            <span className="px-2 py-0.5 rounded border border-primary/30 text-xs font-mono text-primary">
              v{DEFAULT_VERSION}
            </span>
            <div className="hidden md:flex items-center gap-6">
              <Link 
                href="/wiki/home" 
                className={`text-sm font-medium transition-all hover:text-primary hover:glow-text ${
                  isActive('/wiki') ? 'text-primary glow-text' : 'text-muted-foreground'
                }`}
              >
                Wiki
              </Link>
              {isAuthenticated && (
                <>
                  <Link 
                    href="/characters" 
                    className={`text-sm font-medium transition-all hover:text-primary ${
                      isActive('/characters') ? 'text-primary glow-text' : 'text-muted-foreground'
                    }`}
                  >
                    <span className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      Characters
                    </span>
                  </Link>
                  <Link 
                    href="/ships" 
                    className={`text-sm font-medium transition-all hover:text-primary ${
                      isActive('/ships') ? 'text-primary glow-text' : 'text-muted-foreground'
                    }`}
                  >
                    <span className="flex items-center gap-1">
                      <Rocket className="h-4 w-4" />
                      Ships
                    </span>
                  </Link>
                  <Link 
                    href="/games" 
                    className={`text-sm font-medium transition-all hover:text-primary ${
                      isActive('/games') ? 'text-primary glow-text' : 'text-muted-foreground'
                    }`}
                  >
                    <span className="flex items-center gap-1">
                      <Gamepad2 className="h-4 w-4" />
                      Campaigns
                    </span>
                  </Link>
                </>
              )}
              {isOwner && (
                <Link 
                  href="/admin" 
                  className={`text-sm font-medium transition-all hover:text-primary ${
                    isActive('/admin') ? 'text-primary glow-text' : 'text-muted-foreground'
                  }`}
                >
                  <span className="flex items-center gap-1">
                    <Settings className="h-4 w-4" />
                    Admin
                  </span>
                </Link>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <form onSubmit={handleSearch} className="hidden sm:block">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search wiki..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 text-sm bg-muted/50 border border-primary/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 w-64 placeholder:text-muted-foreground/60"
                />
              </div>
            </form>
            
            {mounted && (
              <button
                onClick={() => {
                  const cycle = { dark: 'dusk', dusk: 'light', light: 'dark' } as Record<string, string>;
                  setTheme(cycle[theme || 'dark'] || 'dark');
                }}
                className="p-2 rounded-lg hover:bg-muted transition-all hover:glow-border"
                aria-label="Toggle theme"
                title={`Theme: ${theme}`}
              >
                {theme === 'dark' ? (
                  <Moon className="h-5 w-5 text-primary" />
                ) : theme === 'dusk' ? (
                  <CloudMoon className="h-5 w-5 text-muted-foreground" />
                ) : (
                  <Sun className="h-5 w-5 text-yellow-400" />
                )}
              </button>
            )}

            {!isLoading && (
              isAuthenticated ? (
                <div className="hidden md:flex items-center gap-3">
                  {user?.profileImageUrl && (
                    <img src={user.profileImageUrl} alt="" className="h-7 w-7 rounded-full" />
                  )}
                  {editingName ? (
                    <form onSubmit={async (e) => {
                      e.preventDefault();
                      setSavingName(true);
                      try {
                        await fetch('/api/auth/profile', {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ displayName: nameInput }),
                        });
                        window.location.reload();
                      } finally {
                        setSavingName(false);
                        setEditingName(false);
                      }
                    }} className="flex items-center gap-1">
                      <input
                        type="text"
                        value={nameInput}
                        onChange={e => setNameInput(e.target.value)}
                        placeholder="Display name..."
                        className="w-28 px-2 py-1 text-sm bg-muted/50 border border-primary/20 rounded focus:outline-none focus:ring-1 focus:ring-primary/50"
                        autoFocus
                      />
                      <button type="submit" disabled={savingName} className="p-1 hover:text-primary"><Check className="h-3.5 w-3.5" /></button>
                      <button type="button" onClick={() => setEditingName(false)} className="p-1 hover:text-destructive"><X className="h-3.5 w-3.5" /></button>
                    </form>
                  ) : (
                    <button onClick={() => { setNameInput(user?.displayName || user?.firstName || ''); setEditingName(true); }} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary transition-colors" title="Edit display name">
                      {getUserDisplayName(user)}
                      <Pencil className="h-3 w-3 opacity-50" />
                    </button>
                  )}
                  <a
                    href="/api/logout"
                    className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                  </a>
                </div>
              ) : (
                <a
                  href="/api/login"
                  className="hidden md:flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
                >
                  <LogIn className="h-4 w-4" />
                  Sign In
                </a>
              )
            )}

            <button
              className="md:hidden p-2 rounded-lg hover:bg-muted"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="md:hidden border-t border-primary/10 py-4 space-y-3">
            <Link
              href="/wiki/home"
              className="block px-2 py-2 text-sm hover:text-primary transition-colors"
              onClick={() => setMobileOpen(false)}
            >
              Wiki
            </Link>
            {isAuthenticated && (
              <>
                <Link
                  href="/characters"
                  className="block px-2 py-2 text-sm hover:text-primary transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  Characters
                </Link>
                <Link
                  href="/ships"
                  className="block px-2 py-2 text-sm hover:text-primary transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  Ships
                </Link>
                <Link
                  href="/games"
                  className="block px-2 py-2 text-sm hover:text-primary transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  Campaigns
                </Link>
              </>
            )}
            {isOwner && (
              <Link
                href="/admin"
                className="block px-2 py-2 text-sm hover:text-primary transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                Admin
              </Link>
            )}
            <form onSubmit={(e) => { handleSearch(e); setMobileOpen(false); }} className="sm:hidden">
              <input
                type="text"
                placeholder="Search wiki..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 text-sm bg-muted/50 border border-primary/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </form>
            {!isLoading && (
              isAuthenticated ? (
                <div className="flex items-center justify-between px-2">
                  <span className="text-sm text-muted-foreground">
                    {getUserDisplayName(user)}
                  </span>
                  <a href="/api/logout" className="text-sm text-muted-foreground hover:text-primary">
                    Sign Out
                  </a>
                </div>
              ) : (
                <a
                  href="/api/login"
                  className="block px-2 py-2 text-sm text-primary font-medium"
                >
                  Sign In
                </a>
              )
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
